import React, { CSSProperties, ReactNode } from 'react'

import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu'

import { ReactComponent as CaretRightIcon } from '@app/assets/Small/Outline/CaretRight.svg'
import { bemJoin } from '@app/lib/bem'
import { useAppRoot } from '@app/lib/context/AppRoot'

import { MenuItemContent } from './MenuItemContent'
import { DropdownItem, DropdownMenuAlign, DropdownMenuSize } from './types'

const b = bemJoin('dropdown-menu')

const alignToRadix: Record<DropdownMenuAlign, 'start' | 'end'> = {
  left: 'start',
  right: 'end'
}

const DesktopItem = ({
  item,
  size,
  container
}: {
  item: DropdownItem
  size: DropdownMenuSize
  container: HTMLElement | null
}) => {
  if (item.items?.length) {
    return (
      <DropdownMenuPrimitive.Sub>
        <DropdownMenuPrimitive.SubTrigger asChild disabled={item.disabled}>
          <MenuItemContent
            label={item.label}
            leading={item.leading}
            comment={item.comment}
            trailing={<CaretRightIcon className={b('chevron')} />}
            size={size}
            selected={item.selected}
            disabled={item.disabled}
          />
        </DropdownMenuPrimitive.SubTrigger>
        <DropdownMenuPrimitive.Portal container={container}>
          <DropdownMenuPrimitive.SubContent className={b('content', { sm: true })} sideOffset={4}>
            <DesktopItemList items={item.items} size='sm' container={container} />
          </DropdownMenuPrimitive.SubContent>
        </DropdownMenuPrimitive.Portal>
      </DropdownMenuPrimitive.Sub>
    )
  }

  return (
    <DropdownMenuPrimitive.Item asChild disabled={item.disabled} onSelect={item.onClick}>
      <MenuItemContent
        label={item.label}
        leading={item.leading}
        comment={item.comment}
        size={size}
        selected={item.selected}
        disabled={item.disabled}
      />
    </DropdownMenuPrimitive.Item>
  )
}

const DesktopItemList = ({
  items,
  size,
  container
}: {
  items: DropdownItem[]
  size: DropdownMenuSize
  container: HTMLElement | null
}) => {
  return (
    <>
      {items.map((item) => (
        <DesktopItem key={item.key} item={item} size={size} container={container} />
      ))}
    </>
  )
}

export type DropdownMenuDesktopProps = {
  items: DropdownItem[]
  trigger: ReactNode
  size: DropdownMenuSize
  align: DropdownMenuAlign
  width?: CSSProperties['width']
  matchTriggerWidth?: boolean
  modal?: boolean
  className?: string
}

export const DropdownMenuDesktop = ({
  items,
  trigger,
  size,
  align,
  width,
  matchTriggerWidth,
  modal = false,
  className
}: DropdownMenuDesktopProps) => {
  const { ref: appRootRef } = useAppRoot()

  return (
    <DropdownMenuPrimitive.Root modal={modal}>
      <DropdownMenuPrimitive.Trigger asChild className={className}>
        {trigger}
      </DropdownMenuPrimitive.Trigger>

      <DropdownMenuPrimitive.Portal container={appRootRef.current}>
        <DropdownMenuPrimitive.Content
          className={b('content', { [size]: true, 'match-trigger': matchTriggerWidth })}
          align={alignToRadix[align]}
          sideOffset={4}
          style={{ width }}
        >
          <DesktopItemList items={items} size={size} container={appRootRef.current} />
        </DropdownMenuPrimitive.Content>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  )
}
